/**
 * A date of birth a person can type OR PASTE (2026-10-30).
 *
 * The browser's own date control (`<input type="date">`) refuses a pasted
 * "03/05/1942" in most browsers: the paste lands nowhere and the field stays
 * empty, so a PA copying the date from a face sheet or CounselEAR could not
 * get past it. This is a plain text field that reads the shapes people use,
 * shows back the date it understood, and hands the caller the ISO date
 * (YYYY-MM-DD) the server takes -- or '' while it cannot read one.
 *
 *   03/05/1942  3/5/1942  03-05-1942  03.05.1942  03051942   month first (US)
 *   1942-03-05  1942/03/05  1942-03-05T00:00:00  19420305     year first
 *
 * What is pasted is normalised first (2026-10-30): NFKC folds full-width digits and
 * the full-width solidus (other look-alike slashes are not folded), and the invisible marks Outlook and Windows put around a copied
 * date -- zero-width spaces and joiners, left-to-right / right-to-left marks and
 * embeddings, directional isolates, the byte-order mark -- are removed. A date that
 * looks right but carries them would otherwise be "cannot read".
 *
 * A two-digit year is refused, never guessed (is "42" 1942 or 2042?). A date
 * in the future or before 1900 is refused, as the server refuses it.
 */
import React, { useEffect, useId, useRef, useState } from 'react';

export interface DobReading { iso: string | null; problem: string | null; empty: boolean }

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const FORMAT_HINT = 'Type or paste it as MM/DD/YYYY, for example 03/05/1942.';

const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Read what a person typed or pasted. Pure: tested on its own. */
/** U+200B..U+200F, U+202A..U+202E, U+2066..U+2069, U+FEFF: invisible in a pasted date, and not part of it. */
const INVISIBLE = /[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g;

export function readDob(raw: string, today: string = todayIso()): DobReading {
  let t = String(raw || '').normalize('NFKC').replace(INVISIBLE, '').trim();
  if (!t) return { iso: null, problem: null, empty: true };
  // A pasted timestamp keeps only its date ("1942-03-05T00:00:00", "03/05/1942 12:00 AM").
  t = t.replace(/[T\s]+\d{1,2}:\d{2}.*$/i, '').trim();
  let y: number; let m: number; let d: number;
  let hit = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(t);
  if (hit) { y = Number(hit[1]); m = Number(hit[2]); d = Number(hit[3]); } else {
    hit = /^(\d{1,2})\s*[-/.]\s*(\d{1,2})\s*[-/.]\s*(\d{2}|\d{4})$/.exec(t);
    if (hit && hit[3].length === 2) return { iso: null, problem: 'Write the year with four digits, for example 1942.', empty: false };
    // Eight digits: year first when they start 19 or 20 (no month is 19 or 20), else month first.
    const compact = /^(19|20)\d{6}$/.test(t) ? /^(\d{4})(\d{2})(\d{2})$/.exec(t) : null;
    if (!hit && !compact) hit = /^(\d{2})(\d{2})(\d{4})$/.exec(t);
    if (compact) { y = Number(compact[1]); m = Number(compact[2]); d = Number(compact[3]); } else {
      if (!hit) return { iso: null, problem: `eMerge cannot read that as a date. ${FORMAT_HINT}`, empty: false };
      m = Number(hit[1]); d = Number(hit[2]); y = Number(hit[3]);
    }
  }
  const iso = `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const probe = new Date(`${iso}T00:00:00Z`);
  if (m < 1 || m > 12 || Number.isNaN(probe.getTime()) || probe.toISOString().slice(0, 10) !== iso) {
    return { iso: null, problem: 'There is no such date. Check the month and the day (month first: MM/DD/YYYY).', empty: false };
  }
  if (y < 1900) return { iso: null, problem: 'A date of birth before 1900 is not accepted.', empty: false };
  if (iso > today) return { iso: null, problem: 'A date of birth cannot be in the future.', empty: false };
  return { iso, problem: null, empty: false };
}

/** "March 5, 1942", from an ISO date, without any time zone shifting it. */
export const longDate = (iso: string): string => `${MONTHS[Number(iso.slice(5, 7)) - 1]} ${Number(iso.slice(8, 10))}, ${iso.slice(0, 4)}`;

export interface DobInputProps {
  /** The ISO date the caller holds ('' for none). A change from outside (a reset) replaces what is typed. */
  value: string;
  /** Called with the ISO date, or '' while what is typed is not a date; `problem` says why in words. */
  onChange: (iso: string, problem: string | null) => void;
  'aria-label': string;
  id?: string;
  name?: string;
  autoComplete?: string;
  required?: boolean;
  disabled?: boolean;
  /** Class for the line under the field (what was understood, or what is wrong). */
  hintClassName?: string;
}

export default function DobInput({ value, onChange, id, name, autoComplete = 'bday', required, disabled, hintClassName, ...rest }: DobInputProps) {
  const [text, setText] = useState(value || '');
  const [touched, setTouched] = useState(false);
  const emitted = useRef<string>(value || '');
  const auto = useId().replace(/:/g, '');
  const hintId = `${id || auto}-hint`;

  // A value set from outside -- a reset to '' after a save, a prefill -- replaces the text.
  useEffect(() => {
    if ((value || '') !== emitted.current) {
      emitted.current = value || '';
      setText(value || '');
      setTouched(false);
    }
  }, [value]);

  const reading = readDob(text);
  const take = (next: string) => {
    setText(next);
    const r = readDob(next);
    const iso = r.iso || '';
    emitted.current = iso;
    onChange(iso, r.problem);
  };
  // Say what is wrong once the person has left the field or pasted (a paste is a whole date), never mid-typing.
  const showProblem = Boolean(reading.problem) && touched;

  return (
    <>
      <input
        id={id}
        name={name}
        type="text"
        inputMode="numeric"
        autoComplete={autoComplete}
        placeholder="MM/DD/YYYY"
        value={text}
        disabled={disabled}
        aria-label={rest['aria-label']}
        aria-required={required ? 'true' : undefined}
        aria-invalid={showProblem ? 'true' : undefined}
        aria-describedby={hintId}
        maxLength={40}
        onChange={(e) => take(e.target.value)}
        onBlur={() => setTouched(true)}
        onPaste={() => setTouched(true)}
      />
      <small id={hintId} className={hintClassName} aria-live="polite">
        {reading.iso ? `Reads as ${longDate(reading.iso)} (${reading.iso}).` : showProblem ? reading.problem : FORMAT_HINT}
      </small>
    </>
  );
}
